import React from 'react';
import { useQuery, ApolloError } from '@apollo/client';
import { StackShim as Stack } from '@fluentui/react-migration-v8-v9';
import {
  FluentProvider,
  Label,
  Spinner,
  webLightTheme,
  makeStyles,
  mergeClasses,
} from '@fluentui/react-components';
import UnauthorizedPage from './UnauthorizedPage';
import { GetUserQuery, StatUser } from '../../utils/statApi/UsersApi';
import { verticalCenterStack } from '../../app/common/styles/CommonStyleObjects';
import ErrorPage from '../errors/ErrorPage';
import { UserProvider } from '../../utils/authorization/UserContext';

interface IAuthorizationCheckerProps {
  graphId: string;
  children: JSX.Element;
  useCardStyle?: boolean;
}

const useStyles = makeStyles({
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
});

const AuthorizationChecker = (props: IAuthorizationCheckerProps): JSX.Element => {
  const { graphId, children, useCardStyle } = props;
  const classes = useStyles();
  const {
    error,
    loading,
    data,
  }: { error?: ApolloError; loading: boolean; data: { statUser: StatUser } } = useQuery(
    GetUserQuery,
    {
      variables: { graphGUID: graphId, stampLogin: true },
    },
  );

  if (error && error.networkError) {
    // This is due to the way the networkError object is a union of a few
    // types.  When a union is used, only the common types able to be accessed
    // by code.  So we can't access the statusCode property here without some
    // trickery.  I tried a couple scenarios with typeguards, but this was the
    // only one they would get past check-types and linter errors.
    // https://github.com/apollographql/apollo-link/issues/300
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { networkError }: any = error;
    if ('statusCode' in networkError && networkError.statusCode === 401) {
      return <UnauthorizedPage useCardStyle={useCardStyle} />;
    }
  }

  if (error) {
    return <ErrorPage message={error.message} useCardStyle={useCardStyle} />;
  }

  if (loading) {
    return (
      <div
        className={mergeClasses(
          classes.loadingContainer,
          useCardStyle ? undefined : verticalCenterStack,
        )}
      >
        <Stack horizontal tokens={{ childrenGap: 10 }}>
          <Label>Loading...</Label>
          <FluentProvider theme={webLightTheme}>
            <Spinner aria-label="Loading" />
          </FluentProvider>
        </Stack>
      </div>
    );
  }

  if (!loading && (!data?.statUser || !data?.statUser?.isActive)) {
    return <UnauthorizedPage useCardStyle={useCardStyle} />;
  }

  return <UserProvider value={data.statUser}>{children}</UserProvider>;
};

export default AuthorizationChecker;
